import {
  CalendarPlus,
  CalendarX,
  ClipboardList,
  Euro,
  FilePenLine,
  FileText,
  GraduationCap,
  MessageCircle,
  Paperclip,
  Receipt,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

// ─── Accueil : gestes de « Action rapide » (18/10/2026) ─────────────────────────────────────────
// Mécanique reprise de la maquette accueil-2-carte-blanche.html : un geste qui demande un élève se
// place en pastille dans la recherche (« Encaisser × ») ; choisir l'élève le lance. Les autres
// s'ouvrent aussitôt. 11 gestes possibles, 6 affichés au plus et 1 au moins, choix gardé sur
// l'ordinateur pour chaque utilisateur et chaque école.

export type QuickActionId = 'inscrire' | 'encaisser' | 'planifier' | 'examen' | 'devis' | 'commande' | 'facture' | 'message' | 'documents' | 'contrat' | 'absence';

export type QuickAction = {
  id: QuickActionId;
  label: string;
  icon: LucideIcon;
  /** Geste lancé pour un élève : il passe par la recherche. Sinon, il s'ouvre aussitôt. */
  student?: {
    /** Verbe en bout de ligne des résultats (« Encaisser »). */
    verb: string;
    /** En-tête des résultats (« Encaisser pour… »). */
    head: string;
  };
};

export const QUICK_ACTIONS: readonly QuickAction[] = [
  { id: 'inscrire', label: 'Inscrire un élève', icon: UserPlus },
  { id: 'encaisser', label: 'Encaisser', icon: Euro, student: { verb: 'Encaisser', head: 'Encaisser pour…' } },
  { id: 'planifier', label: 'Planifier une leçon', icon: CalendarPlus, student: { verb: 'Planifier', head: 'Planifier une leçon pour…' } },
  { id: 'examen', label: 'Demander un examen', icon: GraduationCap, student: { verb: 'Demander', head: 'Demander un examen pour…' } },
  { id: 'devis', label: 'Créer un devis', icon: FileText, student: { verb: 'Créer le devis', head: 'Créer un devis pour…' } },
  { id: 'commande', label: 'Créer une commande', icon: ClipboardList, student: { verb: 'Créer la commande', head: 'Créer une commande pour…' } },
  // Une facture est toujours celle d'une commande : le geste ouvre les commandes de l'élève.
  { id: 'facture', label: 'Facturer une commande', icon: Receipt, student: { verb: 'Facturer', head: 'Facturer une commande de…' } },
  { id: 'message', label: 'Écrire à un élève', icon: MessageCircle, student: { verb: 'Écrire', head: 'Écrire à…' } },
  { id: 'documents', label: 'Demander des documents', icon: Paperclip, student: { verb: 'Demander', head: 'Demander des documents à…' } },
  { id: 'contrat', label: 'Préparer un contrat', icon: FilePenLine, student: { verb: 'Préparer', head: 'Préparer un contrat pour…' } },
  { id: 'absence', label: 'Déclarer une absence', icon: CalendarX },
];

/** Raison affichée quand l'encaissement n'est pas permis à ce compte (F53) : bouton grisé, rien d'ouvert. */
export const ENCAISSER_REFUSED = 'Droits « Saisir » et « Valider un encaissement » requis';

export const QUICK_DEFAULT: readonly QuickActionId[] = ['inscrire', 'encaisser', 'planifier', 'examen'];
export const QUICK_MAX = 6;

export const quickAction = (id: QuickActionId) => QUICK_ACTIONS.find((a) => a.id === id)!;
export const quickKey = (userId: string, schoolId: string) => `permio:accueil:actions:${userId}:${schoolId}`;

/** Gestes gardés pour ce compte et cette école (ordre de la liste, 6 au plus) ; sinon ceux par défaut. */
export function readQuickActions(key: string): QuickActionId[] {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (Array.isArray(saved)) {
      const known = QUICK_ACTIONS.map((a) => a.id).filter((id) => saved.includes(id));
      if (known.length) return known.slice(0, QUICK_MAX);
    }
  } catch {
    // Lecture impossible : gestes par défaut.
  }
  return [...QUICK_DEFAULT];
}

export function saveQuickActions(key: string, chosen: QuickActionId[] | null) {
  try {
    if (chosen) localStorage.setItem(key, JSON.stringify(chosen));
    else localStorage.removeItem(key);
  } catch {
    // Stockage bloqué : le choix vaut pour cette visite.
  }
}

/** Ajoute ou retire un geste, en gardant l'ordre de la liste, 6 au plus et 1 au moins. */
export function toggleQuickAction(chosen: QuickActionId[], id: QuickActionId): QuickActionId[] {
  const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
  if (!next.length || next.length > QUICK_MAX) return chosen;
  return QUICK_ACTIONS.map((a) => a.id).filter((x) => next.includes(x));
}

/** Nom tapé dans la recherche, repris dans « Ajouter un élève » : un seul mot = le nom ; sinon prénom puis nom. */
export function nameFromSearch(text: string): { firstName: string; lastName: string } | null {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length || /[\d@]/.test(text)) return null;
  return words.length === 1 ? { firstName: '', lastName: words[0] } : { firstName: words[0], lastName: words.slice(1).join(' ') };
}

/** Élève à inscrire d'après la recherche : nom, téléphone et e-mail reconnus dans le texte tapé. */
export type NewStudent = { firstName: string; lastName: string; phoneNumber: string; email: string };

/**
 * Numéro de téléphone complet : 10 chiffres en 0X (France), +33 / 0033 suivi de 9 chiffres, ou un
 * autre numéro international en « + ». Espaces, points, tirets et « (0) » ignorés. Un NEPH (12 chiffres)
 * ou un numéro incomplet n'en est pas un.
 */
export function isPhoneNumber(text: string): boolean {
  const digits = text.replace(/\(0\)/g, '').replace(/[\s.()/-]/g, '');
  return /^0[1-9]\d{8}$/.test(digits) || /^(?:\+|00)33[1-9]\d{8}$/.test(digits) || /^\+(?!33)[1-9]\d{7,14}$/.test(digits);
}

/**
 * Texte de la recherche découpé pour « Ajouter un élève » : un e-mail (mot avec « @ » et un domaine)
 * et un téléphone complet sont reconnus où qu'ils soient ; le reste est le nom (règle de `nameFromSearch`).
 * `null` quand il reste des chiffres ou un « @ » (NEPH, numéro incomplet…) ou que rien n'est reconnu.
 */
export function studentFromSearch(text: string): NewStudent | null {
  let rest = ` ${text} `;
  const email = rest.match(/\s([^\s@]+@[^\s@]+\.[^\s@]+)(?=\s)/)?.[1] ?? '';
  if (email) rest = rest.replace(email, ' ');
  const phone = rest.match(/\+?\d[\d\s.()/-]*\d/)?.[0] ?? '';
  const phoneNumber = phone && isPhoneNumber(phone) ? phone.trim() : '';
  if (phoneNumber) rest = rest.replace(phone, ' ');
  if (/[\d@]/.test(rest)) return null;
  const name = nameFromSearch(rest) ?? { firstName: '', lastName: '' };
  if (!name.lastName && !phoneNumber && !email) return null;
  return { ...name, phoneNumber, email };
}

/** Liste des élèves avec la fenêtre « Ajouter un apprenant » ouverte, préremplie du nom, du téléphone et de l'e-mail tapés. */
export function newStudentHref(text: string): string {
  const student = studentFromSearch(text);
  const params = new URLSearchParams({ ajouter: '1' });
  if (student?.firstName) params.set('prenom', student.firstName);
  if (student?.lastName) params.set('nom', student.lastName);
  if (student?.phoneNumber) params.set('tel', student.phoneNumber);
  if (student?.email) params.set('email', student.email);
  return `/candidates?${params}`;
}

/** Libellés de la proposition « Créer l'élève » (recherche Ctrl K) : ce qui est prérempli, ce qui reste à saisir. */
export function newStudentLabels(student: NewStudent): { title: string; detail: string } {
  const name = [student.firstName, student.lastName].filter(Boolean).join(' ');
  const contact = [student.phoneNumber, student.email].filter(Boolean).join(' · ');
  const filled = [
    student.firstName && 'prénom',
    student.lastName && 'nom',
    student.phoneNumber && 'téléphone',
    student.email && 'e-mail',
  ].filter(Boolean) as string[];
  const missing = [!student.firstName && 'prénom', !student.lastName && 'nom'].filter(Boolean) as string[];
  const list = filled.length > 1 ? `${filled.slice(0, -1).join(', ')} et ${filled[filled.length - 1]}` : filled[0];
  return {
    title: name ? `Créer l’élève « ${name} »` : `Créer un élève « ${contact} »`,
    detail:
      `${list.charAt(0).toUpperCase()}${list.slice(1)} prérempli${filled.length > 1 ? 's' : ''}, ` +
      (missing.length ? `à compléter du ${missing.join(' et du ')}.` : 'à vérifier.'),
  };
}
